/* Tweaks — small user-preference surface surfaced by the floating
   TweaksPanel. Persisted to localStorage under `daedalus.tweaks`.

   Phase 0.5 ports the subset the plan calls out:
     theme    · light | dark
     density  · spacious | balanced | dense
     accent   · one of five swatches
     motion   · on | off
     chatW    · slider over --chat-dock-w

   Applied as CSS custom properties on :root so any rule that already
   reads `--accent` / `--d` / `--chat-dock-w` picks up the change
   without a React re-render. Motion off sets --morph-dur to 1ms so
   the FlowScene morph reduces to an instant layout swap. */

export type Theme = 'light' | 'dark';
export type Density = 'spacious' | 'balanced' | 'dense';

export interface Tweaks {
  theme: Theme;
  density: Density;
  accent: string;
  motion: boolean;
  chatW: number;
}

export const DEFAULT_TWEAKS: Tweaks = {
  theme: 'light',
  density: 'balanced',
  accent: '#1f4fd9', // survey blue — matches tokens.css --accent default
  motion: true,
  chatW: 360,
};

export const ACCENT_SWATCHES: { value: string; name: string }[] = [
  { value: '#1f4fd9', name: 'survey blue' },
  { value: '#0e1a2b', name: 'deep ink' },
  { value: '#2e6a3a', name: 'drafting green' },
  { value: '#b0382a', name: 'stamp red' },
  { value: '#9a7410', name: 'pencil gold' },
];

const STORAGE_KEY = 'daedalus.tweaks';

export function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_TWEAKS, ...touchDefaults() };
    return { ...DEFAULT_TWEAKS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_TWEAKS };
  }
}

/* First run on a touch-first device: default density to spacious so tap
   targets start ~8% larger everywhere the --d multiplier is consumed.
   Only applies when nothing is saved — an explicit user choice from the
   Settings scene always wins. */
function touchDefaults(): Partial<Tweaks> {
  try {
    return window.matchMedia('(pointer: coarse)').matches
      ? { density: 'spacious' }
      : {};
  } catch {
    return {};
  }
}

export function saveTweaks(t: Tweaks): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

const DENSITY_MULTIPLIER: Record<Density, number> = {
  spacious: 1.08,
  balanced: 1,
  dense: 0.88,
};

export function applyTweaks(t: Tweaks): void {
  const root = document.documentElement;
  root.classList.remove('theme-light', 'theme-dark');
  root.classList.add(t.theme === 'dark' ? 'theme-dark' : 'theme-light');
  root.style.setProperty('--accent', t.accent);
  root.style.setProperty('--d', String(DENSITY_MULTIPLIER[t.density]));
  root.style.setProperty('--morph-dur', t.motion ? '900ms' : '1ms');
  root.style.setProperty('--chat-dock-w', `${t.chatW}px`);
}
